import * as React from "react";
import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Fade from "@mui/material/Fade";
import styled from "styled-components";
import { Flex, Button, ButtonClass, Loading } from "src/components/ui";
import closeIcon from "src/assets/images/common/closeIcon.svg";
import { theme } from "src/styles/Theme";
import { toast, ToastContainer } from "react-toastify";
import { ArtisansServices } from "src/service/ArtisansServices";

const Wrapper = styled.div`
  .heading {
    background-color: ${(props) => props.theme.colors.gray_04};
    padding: 10px 20px;
    height: 56px;
    align-items: center;
    display: flex;
    border-radius: 8px 8px 0 0;

    img {
      padding: 8px;
      &:hover {
        cursor: pointer;
        background-color: ${(props) => props.theme.colors.lilac};
        transition: 0.3s;
        border-radius: 50%;
      }
    }

    p {
      font-weight: 700;
      font-size: 16px;
      color: ${(props) => props.theme.colors.text_01};
    }
  }

  .content {
    padding: 20px;

    .legend {
      font-size: 13px;
      margin-bottom: 15px;
      color: ${(props) => props.theme.colors.text_02};
    }

    .otp-container {
      display: flex;
      gap: 10px;
      margin-bottom: 20px;
      justify-content: center;
    }

    .otp-input {
      width: 45px;
      height: 45px;
      text-align: center;
      font-size: 18px;
      border-radius: 8px;
      border: 1px solid ${(props) => props.theme.colors.text_02};
      outline: none;

      &:focus {
        border: 1px solid ${(props) => props.theme.colors.purple};
      }
    }
  }
`;

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 420,
  maxWidth: "90%",
  bgcolor: theme.colors.white,
  borderRadius: "8px",
  boxShadow: 24,
  outline: "none",
};

export const OtpAdmin: React.FC<{
  open: boolean;
  handleClose: () => void;
  partnerId: string;
}> = ({ open, handleClose, partnerId }) => {
  const [otp, setOtp] = React.useState(["", "", "", ""]);
  const [isLoading, setIsLoading] = React.useState(false);

  const inputsRef = React.useRef<Array<HTMLInputElement | null>>([]);

  const handleChange = (value: string, index: number) => {
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);

    if (value && index < 3) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const submitHandler = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const code = Number(otp.join(""));

    const payload = {
      id: partnerId,
      code: code,
    };

    ArtisansServices.otpWithdrawalAdmin(payload)
      .then((res: any) => {
        toast.success(res?.data?.message || "Verified successfully");
        handleClose();
      })
      .catch((err: any) => {
        toast.error(
          err?.response?.data?.error?.message || "Invalid or expired OTP"
        );
        console.log(err)
      })
      .finally(() => setIsLoading(false));
  };

  const isDisabled = otp.some((digit) => digit === "");

  return (
    <>
      <ToastContainer />

      <Modal open={open} onClose={handleClose} closeAfterTransition BackdropComponent={Backdrop}>
        <Fade in={open}>
          <Box sx={style}>
            <Wrapper>
              <div className="heading">
                <Flex justify="space-between" align="center" style={{ width: "100%" }}>
                  <p>Verify Withdrawal</p>
                  <img src={closeIcon} alt="close" onClick={handleClose} />
                </Flex>
              </div>

              <div className="content">
                <p className="legend">
                  Check your email for the 4-digit verification code.
                </p>

                <form onSubmit={submitHandler}>
                  <div className="otp-container">
                    {otp.map((digit, index) => (
                      <input
                        key={index}
                        ref={(el) => (inputsRef.current[index] = el)}
                        className="otp-input"
                        type="text"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleChange(e.target.value, index)}
                        onKeyDown={(e) => handleKeyDown(e, index)}
                      />
                    ))}
                  </div>

                  <Button
                    type="submit"
                    classes={[ButtonClass.SOLID]}
                    disabled={isDisabled || isLoading}
                    style={{
                      backgroundColor: theme.colors.purple,
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    {isLoading ? <Loading color="white" /> : "Verify"}
                  </Button>
                </form>
              </div>
            </Wrapper>
          </Box>
        </Fade>
      </Modal>
    </>
  );
};

export default OtpAdmin;